import React from 'react';
import { IEvaluation } from '@/lib/evaluationsApi';
import * as Styled from './styled';
import { Stack, Row } from '@/ui/primatives';
import { formatDateTime } from '@/lib/dateTime';

import ProgressIndicator from '@/components/ProgressIndicator';
import { PromptDisplay } from '../PromptEditor';
import { SourceFileInfo } from '../EvaluationFileInfo';
import * as EvaluationTag from '../../EvaluationTag';

import { IRepository } from '@/types';
import OutputFileInfo from './OutputFileInfo';

const EvaluationStatus: React.FC<{ evaluation: IEvaluation; repository: IRepository }> = ({
  evaluation,
  repository,
}) => {
  return evaluation.status !== 'completed' ? (
    <ProgressIndicator
      current={evaluation.progress.processed}
      total={evaluation.progress.total}
      currentFormatFunction={(value) => `${value} rows processed`}
      totalFormatFunction={(value) => `of ${value} rows`}
    />
  ) : (
    <OutputFileInfo evaluation={evaluation} repository={repository} />
  );
};

const BodyPane: React.FC<{ repository: IRepository; evaluation: IEvaluation }> = ({
  repository,
  evaluation,
  ...rest
}) => {
  const isSample = evaluation.is_sample;
  return (
    <Stack gap={20} {...rest}>
      <Stack gap={5} style={{ flex: 1 }}>
        <label>Prompt</label>
        <PromptDisplay prompt={evaluation.prompt} />
      </Stack>
      {!isSample && <EvaluationStatus evaluation={evaluation} repository={repository} />}
    </Stack>
  );
};

const ReadOnlyView: React.FC<{ repository: IRepository; evaluation: IEvaluation }> = ({ repository, evaluation }) => {
  return (
    <Styled.ReadOnlyView>
      <Styled.SplitFormWrapper>
        <Stack gap={10} data-tutorialid="evaluation-definition" justifyContent="space-between">
          <Stack gap={10}>
            <div>
              <SourceFileInfo evaluation={evaluation} />
            </div>
          </Stack>
          <Stack gap={10}>
            <div>
              <label>Type:</label> <EvaluationTag.Type pill evaluation={evaluation} />
            </div>
            {/* <Row gap={5} alignItems="center">
              <label>Developer:</label> <EvaluationTag.Developer variant="white" pill border evaluation={evaluation} />
              </Row> */}
            <Row gap={5} alignItems="center">
              <label>Model:</label> <EvaluationTag.ModelDeveloper pill evaluation={evaluation} />
            </Row>
            <Row gap={5} alignItems="center">
              <label>Provider:</label> <EvaluationTag.Provider pill evaluation={evaluation} />
            </Row>
          </Stack>
          <div>
            <label>Target field:</label> {evaluation.target_column}
          </div>
        </Stack>
        <BodyPane repository={repository} evaluation={evaluation} data-tutorialid="evaluation-prompt" />
        <Stack gap={10} data-tutorialid="evaluation-status" style={{ maxWidth: '25%' }}>
          <Row gap={5} alignItems="center" justifyContent="space-between">
            <label>Queued: </label> <span className="text-line-truncate">{formatDateTime(evaluation.queued_at)}</span>
          </Row>
          {evaluation.status === 'completed' && (
            <Row gap={5} alignItems="center" justifyContent="space-between">
              <label>Completed: </label>{' '}
              <span className="text-line-truncate">{formatDateTime(evaluation.completed_at!)}</span>
            </Row>
          )}
          {evaluation.status === 'started' && (
            <Row gap={5} alignItems="center" justifyContent="space-between">
              <label>Started: </label>{' '}
              <span className="text-line-truncate">{formatDateTime(evaluation.started_at!)}</span>
            </Row>
          )}
          <div className="nowrap">
            <EvaluationTag.RunTime evaluation={evaluation} />
          </div>
          <div>
            <EvaluationTag.Rows evaluation={evaluation} />
          </div>
          <Row gap={10} alignItems="center">
            <EvaluationTag.Tokens evaluation={evaluation} />
            <EvaluationTag.Cost evaluation={evaluation} />
          </Row>
        </Stack>
      </Styled.SplitFormWrapper>
    </Styled.ReadOnlyView>
  );
};

export default ReadOnlyView;
